/**
 * NSFW Rule Presets Configuration
 * 
 * Light/Medium/Strict are UI presets only.
 * They expand into numeric rule thresholds that can be tweaked during testing.
 * The frontend sends these expanded rules to Convex, which makes the final decision.
 */

export interface ThresholdCondition {
  gte?: number;  // greater than or equal
  lte?: number;  // less than or equal
}

export interface RuleCondition {
  P?: ThresholdCondition;  // Porn
  S?: ThresholdCondition;  // Sexy
  H?: ThresholdCondition;  // Hentai
  N?: ThresholdCondition;  // Neutral
  D?: ThresholdCondition;  // Drawing / anime indicator
}

export interface NsfwRuleSet {
  blockIfAny: RuleCondition[];
}

export type NsfwPresetLevel = "light" | "medium" | "strict";
export type AccuracyLevel = 0 | 1 | 2; // 0=accurate, 1=balanced, 2=fast

/**
 * BlueJay Config Interface
 * This is the simplified format used for reading/writing browser rules
 */
export interface BlueJayConfig {
  nsfwLevel: NsfwPresetLevel;
  human: boolean;
  accuracy: AccuracyLevel;
}

/**
 * Default BlueJay configuration values
 */
export const DEFAULT_BLUEJAY_CONFIG: BlueJayConfig = {
  nsfwLevel: "medium",
  human: false,
  accuracy: 2, // fast
};

/**
 * NSFW Rule Presets
 * 
 * These numeric thresholds can be easily modified during testing.
 * The frontend sends the full expanded rules to the backend.
 */
export const NSFW_PRESETS: Record<NsfwPresetLevel, NsfwRuleSet> = {
  // Light: Block only explicit adult content
  light: {
    blockIfAny: [
      { P: { gte: 0.70 } },           // Block if Porn >= 70%
      { H: { gte: 0.70 } },           // Block if Hentai >= 70%
    ]
  },
  
  // Medium: Block explicit + suggestive imagery
  medium: {
    blockIfAny: [
      { P: { gte: 0.50 } },           // Block if Porn >= 50%
      { H: { gte: 0.50 } },           // Block if Hentai >= 50%
      { S: { gte: 0.50 } },           // Block if Sexy >= 50%
      { P: { gte: 0.30 }, S: { gte: 0.30 } },  // Block if Porn >= 30% AND Sexy >= 30%
      { H: { gte: 0.30 }, S: { gte: 0.30 } },  // Block if Hentai >= 30% AND Sexy >= 30%
    ]
  },
  
  // Strict: Block immodest or inappropriate dress
  strict: {
    blockIfAny: [
      { P: { gte: 0.40 } },           // Block if Porn >= 40%
      { H: { gte: 0.40 } },           // Block if Hentai >= 40%
      { S: { gte: 0.30 } },           // Block if Sexy >= 30%
      { P: { gte: 0.20 }, S: { gte: 0.20 } },  // Block if Porn >= 20% AND Sexy >= 20%
      { H: { gte: 0.20 }, S: { gte: 0.20 } },  // Block if Hentai >= 20% AND Sexy >= 20%
      { S: { gte: 0.20 }, N: { lte: 0.70 } },  // Block if Sexy >= 20% AND Neutral <= 70%
    ]
  }
} as const;

/**
 * Get the expanded rule set for a preset level
 */
export function getNsfwRulesForPreset(level: NsfwPresetLevel): NsfwRuleSet {
  return NSFW_PRESETS[level];
}
